import { formatExerciseDisplay, getExerciseType, EXERCISE_TYPES } from '../../../utils/exerciseFormatters';
import { formatRecoveryDisplaySeconds } from '../../../utils/formatRecoveryTime';
import { formatWorkoutRepsTarget } from '../../../constants/workoutRepsMode';
import {
    isPyramidExercise,
    formatSeriesPlanSummary,
    ensureSeriesPlan,
    formatWorkoutWeightTarget,
} from '../../../constants/workoutSeriesPlan';

/** Caratteri sicuri per jsPDF/Helvetica (no Unicode tipografico). */
export function pdfSafeText(value) {
    return String(value ?? '')
        .replace(/\u2192/g, '->')
        .replace(/[\u2018\u2019]/g, "'")
        .replace(/[\u201c\u201d]/g, '"')
        .replace(/[\u2013\u2014]/g, '-');
}

export const PDF_SET_TYPE_LABELS = {
    superset: 'SUPERSET',
    circuit: 'CIRCUIT',
    giant_set: 'GIANT SET',
    dropset: 'DROPSET',
    triset: 'TRISET',
    piramidale: 'PIRAMIDALE',
};

export const PDF_SET_TYPE_COLORS = {
    superset: [147, 51, 234],
    circuit: [239, 68, 68],
    giant_set: [168, 85, 247],
    dropset: [249, 115, 22],
    triset: [234, 179, 8],
    piramidale: [59, 130, 246],
};

export function getPdfSetTypeLabel(setType) {
    const key = (setType || '').toLowerCase();
    return PDF_SET_TYPE_LABELS[key] || null;
}

export function getPdfSetTypeColor(setType) {
    const key = (setType || '').toLowerCase();
    return PDF_SET_TYPE_COLORS[key] || [147, 51, 234];
}

/** Righe dettaglio esercizio per template griglia (stesse regole del template standard). */
export function buildPdfGridExerciseDetailLines(esercizio) {
    const exerciseType = getExerciseType(esercizio);
    const isCardio = exerciseType === EXERCISE_TYPES.CARDIO;
    const isIsometric = exerciseType === EXERCISE_TYPES.ISOMETRIC;

    if (isPyramidExercise(esercizio)) {
        const repsDisplay = formatSeriesPlanSummary(ensureSeriesPlan(esercizio), { pdf: true });
        const pesoDisplay = formatWorkoutWeightTarget(esercizio, { pdf: true });
        const lines = [pdfSafeText(`Piramidale: ${repsDisplay}`)];
        if (pesoDisplay !== '0') {
            lines.push(pdfSafeText(`Peso: ${pesoDisplay} kg`));
        }
        const rec = parseFloat(esercizio.tempo_recupero) || 0;
        if (rec > 0) {
            lines.push(pdfSafeText(`Recupero: ${formatRecoveryDisplaySeconds(rec)}`));
        }
        return lines;
    }

    if (isCardio) {
        const lines = [pdfSafeText(formatExerciseDisplay(esercizio.ripetizioni, esercizio))];
        if (esercizio.bpm && parseInt(esercizio.bpm, 10) > 0) {
            lines.push(pdfSafeText(`BPM: ${parseInt(esercizio.bpm, 10)}`));
        }
        if (esercizio.calorie && parseInt(esercizio.calorie, 10) > 0) {
            lines.push(pdfSafeText(`Calorie: ${parseInt(esercizio.calorie, 10)}`));
        }
        const rec = parseFloat(esercizio.tempo_recupero) || 0;
        if (rec > 0) {
            lines.push(pdfSafeText(`Recupero: ${formatRecoveryDisplaySeconds(rec)}`));
        }
        return lines;
    }

    if (isIsometric) {
        const serie = parseInt(esercizio.serie, 10) || 1;
        const displayValue = formatWorkoutRepsTarget(esercizio)
            || formatExerciseDisplay(esercizio.ripetizioni, esercizio);
        const lines = [pdfSafeText(`${serie} serie x ${displayValue}`)];
        const rec = parseFloat(esercizio.tempo_recupero) || 0;
        if (rec > 0) {
            lines.push(pdfSafeText(`Recupero: ${formatRecoveryDisplaySeconds(rec)}`));
        }
        return lines;
    }

    const serie = parseInt(esercizio.serie, 10) || 1;
    const displayValue = formatWorkoutRepsTarget(esercizio)
        || formatExerciseDisplay(esercizio.ripetizioni, esercizio);
    const lines = [pdfSafeText(`${serie} serie x ${displayValue}`)];
    const peso = parseFloat(esercizio.peso) || 0;
    if (peso > 0) {
        lines.push(pdfSafeText(`Peso: ${peso.toFixed(2).replace('.', ',')} kg`));
    }
    const rec = parseFloat(esercizio.tempo_recupero) || 0;
    if (rec > 0) {
        lines.push(pdfSafeText(`Recupero: ${formatRecoveryDisplaySeconds(rec)}`));
    }
    return lines;
}

/** Riga compatta dettagli per template standard (box unico). */
export function buildPdfStandardExerciseDetailsText(esercizio) {
    const rec = formatRecoveryDisplaySeconds(esercizio.tempo_recupero);
    if (isPyramidExercise(esercizio)) {
        const repsDisplay = formatSeriesPlanSummary(ensureSeriesPlan(esercizio), { pdf: true });
        const pesoDisplay = formatWorkoutWeightTarget(esercizio, { pdf: true });
        const pesoText = pesoDisplay !== '0' ? `${pesoDisplay}kg | ` : '';
        return pdfSafeText(`Piramidale: ${repsDisplay} | ${pesoText}${rec}`);
    }
    const repsDisplay = formatWorkoutRepsTarget(esercizio)
        || formatExerciseDisplay(esercizio.ripetizioni, esercizio);
    const pesoDisplay = formatWorkoutWeightTarget(esercizio, { pdf: true });
    const pesoText = pesoDisplay !== '0' ? `${pesoDisplay}kg | ` : '';
    return pdfSafeText(`${esercizio.serie}x${repsDisplay} | ${pesoText}${rec}`);
}
